import API, { createMAX } from "@zillow/mortgage-api-client";
import { getFullNameFromAbbr } from "../../node_modules/@zillow/state-region-id-map/dist/state-region-id-map.esm.esm.js";
import { standardRate, DEFAULT_PARTNER_ID, LOAN_AMOUNT_RATE_BUCKET, RATE_TYPE, LOAN_TO_VALUE_RATE_BUCKET } from "./constants.esm.js";
const _zconfigs = {
  prod: {
    serverURLs: {
      MAX: "https://mortgageapi.zillow.com"
    },
    partnerId: "RD-WXDQSGN"
  },
  default: {
    serverURLs: {
      MAX: "https://mortgageapi.qa.zillow.net"
    },
    partnerId: "RD-GKZRBBL"
  }
};
const setServerUrl = () => {
  const _zconfig = _zconfigs[process.env.NEXT_PUBLIC_ENV] || _zconfigs.default;
  if (!window._zconfig) {
    window._zconfig = _zconfig;
  } else {
    const zconfig = window._zconfig;
    if (!zconfig.partnerId) {
      global.window._zconfig.partnerId = _zconfig.partnerId;
    }
    if (!zconfig.serverURLs) {
      zconfig.serverURLs = {
        MAX: _zconfig.serverURLs.MAX
      };
    } else if (!zconfig.serverURLs.MAX) {
      zconfig.serverURLs.MAX = _zconfig.serverURLs.MAX;
    }
  }
};
const formatCityFromLink = (city) => {
  return city && typeof city === "string" && capitalize(city.replace(/-/g, " "));
};
const capitalize = (str) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};
const createMax = () => {
  if (!API.MAX) {
    if (!window._zconfig.serverURLs.MAX) {
      throw new Error("MAX serverURL is not set");
    }
    createMAX(window._zconfig.serverURLs.MAX);
  }
  return;
};
const stateAbbreviationValidator = (value) => {
  return getFullNameFromAbbr(value) !== void 0;
};
const getCurrentRates = async (inputs) => {
  if (!API || !API.MAX || !API.MAX.GET) {
    return Promise.resolve({
      rates: {
        rateQuery: {
          rate: 0
        }
      }
    });
  }
  try {
    return await API.MAX.GET({
      action: "getCurrentRates",
      timeout: 3e3,
      input: inputs
    });
  } catch (error) {
    return {
      rates: {
        rateQuery: {
          rate: 0
        }
      }
    };
  }
};
function getStandardInterestRate(loanProgram = "Fixed30Year") {
  return standardRate[loanProgram];
}
function getRateQuery({
  loanAmount: loanAmountParam,
  homePrice,
  downPayment,
  loanProgram = "Fixed30Year",
  creditScore = "R_720_739",
  va = false,
  refinance = false,
  fha = false,
  jumbo = false
}) {
  const rateBucketResults = calculateRateBuckets({
    homePrice,
    downPayment,
    loanProgram,
    creditScore
  });
  const rateQuery = {
    stateAbbreviation: API.stateAbbreviation,
    program: rateBucketResults.loanProgram
  };
  if (rateBucketResults.loanAmountBucket !== LOAN_AMOUNT_RATE_BUCKET.UNSURE) {
    rateQuery.loanAmountBucket = rateBucketResults.loanAmountBucket;
    rateQuery.loanToValueBucket = rateBucketResults.loanToValueBucket;
  } else {
    if (homePrice) {
      const loanAmount = loanAmountParam || homePrice - downPayment;
      rateQuery.propertyBucket = {
        propertyValue: homePrice,
        loanAmount,
        location: {
          stateAbbreviation: API.stateAbbreviation
        }
      };
    }
  }
  if (va) rateQuery.loanType = "VA";
  if (rateBucketResults.creditScoreBucket) rateQuery.creditScoreBucket = rateBucketResults.creditScoreBucket;
  if (refinance) rateQuery.refinance = true;
  if (fha) rateQuery.loanType = "FHA";
  if (jumbo) rateQuery.loanType = "Jumbo";
  return rateQuery;
}
function getCreditScoreBucket(creditScore) {
  const buckets = {
    R_760_: "VeryHigh",
    R_740_759: "VeryHigh",
    R_720_739: "High",
    R_700_719: "High",
    R_680_699: "High",
    R_660_679: "Low",
    R_640_659: "Low",
    R_620_639: "Low",
    R_600_619: "Low",
    R_560_599: "Low",
    R_300_559: "Low"
  };
  return creditScore && buckets[creditScore];
}
function getLoanAmountRateBucket(homePrice, downPayment) {
  if (typeof homePrice === "undefined" || typeof downPayment === "undefined") {
    return LOAN_AMOUNT_RATE_BUCKET.UNSURE;
  }
  const remainder = homePrice - downPayment;
  if (remainder < 1e5) {
    return LOAN_AMOUNT_RATE_BUCKET.MICRO;
  } else if (remainder >= 1e5 && remainder < 2e5) {
    return LOAN_AMOUNT_RATE_BUCKET.SMALL_CONFORMING;
  } else if (remainder >= 2e5 && remainder < 453100) {
    return LOAN_AMOUNT_RATE_BUCKET.CONFORMING;
  }
  return LOAN_AMOUNT_RATE_BUCKET.UNSURE;
}
function getLoanToValueRateBucket(homePrice, downPayment) {
  if (typeof homePrice === "undefined" || typeof downPayment === "undefined") {
    return LOAN_TO_VALUE_RATE_BUCKET.NORMAL;
  }
  const downPercent = downPayment / homePrice * 100;
  if (downPercent < 5) {
    return LOAN_TO_VALUE_RATE_BUCKET.VERY_HIGH;
  } else if (downPercent >= 5 && downPercent < 20) {
    return LOAN_TO_VALUE_RATE_BUCKET.HIGH;
  }
  return LOAN_TO_VALUE_RATE_BUCKET.NORMAL;
}
function calculateRateBuckets({
  homePrice,
  downPayment,
  loanProgram,
  creditScore
}) {
  const loanToValueRateBucket = getLoanToValueRateBucket(homePrice, downPayment);
  const loanAmountRateBucket = getLoanAmountRateBucket(homePrice, downPayment);
  const creditScoreBucket = getCreditScoreBucket(creditScore);
  const result = {
    rateType: RATE_TYPE.STANDARD,
    loanProgram,
    loanAmountBucket: loanAmountRateBucket,
    loanToValueBucket: loanToValueRateBucket,
    creditScoreBucket
  };
  if (result.loanAmountBucket !== LOAN_AMOUNT_RATE_BUCKET.CONFORMING || result.loanToValueBucket !== LOAN_TO_VALUE_RATE_BUCKET.NORMAL || result.creditScoreBucket !== "VeryHigh") {
    result.rateType = RATE_TYPE.NON_STANDARD;
  }
  if (homePrice - downPayment < 0) {
    result.rateType = RATE_TYPE.INVALID;
  }
  return result;
}
const fetchInterestRate = async (rateQueryParams) => {
  const {
    loanProgram
  } = rateQueryParams;
  const standardRate2 = getStandardInterestRate(loanProgram);
  const response = await getCurrentRates({
    partnerId: API.partnerId ?? DEFAULT_PARTNER_ID,
    queries: {
      rateQuery: getRateQuery(rateQueryParams)
    }
  });
  const newRate = response.rates.rateQuery.rate;
  if (newRate === 0) {
    return standardRate2;
  }
  return newRate;
};
const fetchRate = async (rateQueryParams) => {
  const rate = await fetchInterestRate(rateQueryParams);
  return rate == 0 ? standardRate.Fixed30Year : rate;
};
export {
  DEFAULT_PARTNER_ID,
  calculateRateBuckets,
  createMax,
  fetchInterestRate,
  fetchRate,
  formatCityFromLink,
  getCurrentRates,
  getLoanAmountRateBucket,
  getLoanToValueRateBucket,
  getRateQuery,
  getStandardInterestRate,
  setServerUrl,
  stateAbbreviationValidator
};
